import { css } from '@emotion/css';
import { For, Show, createSignal } from 'solid-js';
import { backendTheme } from 'Shared/backend/Theme';


/* One choice the panel offers. 'key' is what comes back from choose(); 'note' is a second, quieter
   line under the label ('' for none) and 'current' shows the row in bold. */
export type DrillDownRow = {
	key:     string;
	label:   string;
	note:    string;
	current: boolean;
};

/* A category the panel drills into, shown at the top level as a row with a chevron. 'emptyNote' is
   what the drilled panel says in place of an empty list. */
export type DrillDownGroup = {
	key:       string;
	label:     string;
	emptyNote: string;
	rows:      DrillDownRow[];
};

type IDrillDownSelect = {
	/* Fixed text shown inside the trigger ahead of the label, so a caller's own caption ('Apply to')
	   sits within the clickable area rather than beside it. '' for none. */
	prefix:   string;
	/* What the trigger shows - the current choice, said however the caller wants to say it. */
	label:    string;
	/* The rows offered up front, a horizontal rule drawn between one section and the next. */
	sections: DrillDownRow[][];
	groups:   DrillDownGroup[];
	/* A trigger sized to its own label rather than to a field, for one that reads as a button - an
	   'Add ...' that opens the panel rather than a control showing a current value. */
	compact:  boolean;
	/* A trigger with no box - the label and caret sit inline as plain text, for a de-emphasised
	   control whose value is nearly always the same. */
	plain:    boolean;
	/* Which edge of the trigger the panel lines up with. 'right' for a trigger that sits at the
	   right of its row, so the wider panel opens inwards rather than off the edge. */
	panelAlign: 'left'|'right';
	choose:   (key:string) => void;
};


const wrapStyle = () => css({
	position: 'relative',
	display: 'inline-block',
});

const triggerStyle = (compact:boolean, plain:boolean) => css({
	display: 'flex',
	alignItems: plain ? 'baseline' : 'center',
	justifyContent: plain ? 'flex-start' : 'space-between',
	gap: plain ? '0.35em' : '0.6em',
	minWidth: compact || plain ? undefined : '16em',
	maxWidth: '24em',
	padding: plain ? 0 : '0.35em 0.6em',
	background: plain ? 'none' : 'white',
	border: plain ? 'none' : '1px solid #ccc',
	borderRadius: plain ? 0 : 3,
	color: plain ? backendTheme().palette.label : backendTheme().palette.text,
	font: 'inherit',
	fontSize: plain ? 12 : undefined,
	fontWeight: plain ? 500 : undefined,
	textAlign: 'left',
	cursor: 'pointer',
});

const triggerLabelStyle = () => css({
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	whiteSpace: 'nowrap',
});

const panelStyle = (compact:boolean, plain:boolean, panelAlign:'left'|'right') => css({
	position: 'absolute',
	top: '100%',
	left: panelAlign == 'right' ? 'auto' : 0,
	right: panelAlign == 'right' ? 0 : undefined,
	zIndex: 20,
	marginTop: 2,
	minWidth: compact || plain ? '18em' : '100%',
	maxWidth: '28em',
	maxHeight: '60vh',
	overflowY: 'auto',
	display: 'flex',
	flexDirection: 'column',
	background: 'white',
	border: '1px solid #ccc',
	borderRadius: 3,
	boxShadow: '0 4px 20px rgba(0,0,0,0.25)',

	hr: {
		width: '100%',
		margin: '0.25em 0',
		border: 'none',
		borderTop: '1px solid #ddd',
	},
});

const rowStyle = () => css({
	display: 'flex',
	alignItems: 'baseline',
	gap: '0.5em',
	padding: '0.4em 0.6em',
	background: 'none',
	border: 'none',
	font: 'inherit',
	color: backendTheme().palette.text,
	textAlign: 'left',
	cursor: 'pointer',

	'&:hover': {backgroundColor: backendTheme().palette.dimBackground},
});

const currentRowStyle = () => css({
	fontWeight: 'bold',
});

const leaderStyle = () => css({
	flex: 1,
});

const chevronStyle = () => css({
	color: backendTheme().palette.label,
	fontSize: '0.8em',
});

const noteStyle = () => css({
	color: backendTheme().palette.label,
	fontSize: '0.85em',
	fontStyle: 'italic',
});

const emptyStyle = () => css({
	padding: '0.4em 0.6em',
	color: backendTheme().palette.label,
	fontSize: '0.85em',
});


/*
	A dropdown whose panel offers a handful of rows up front and then a set of categories that drill
	down into rows of their own - so a choice can be made from a shortlist or hunted for in the full
	set. A native <select> can't drill down, hence the hand-built panel. It closes on focus leaving
	the wrapper rather than on a document listener, so nothing here needs onMount.

	The control says nothing about what is being chosen: the caller hands in the rows and gets back
	the key of the one that was clicked. Keys that stand for something other than an item - an 'auto'
	mode, or the current choice offered as a no-op - are the caller's to recognise.
*/
export function DrillDownSelect(P:IDrillDownSelect)
{
	const [open,setOpen] = createSignal(false);
	const [drilled,setDrilled] = createSignal<string|undefined>(undefined);

	const drilledGroup = () => P.groups.find(group => group.key == drilled());

	const close = () => { setOpen(false); setDrilled(undefined); };

	const take = (key:string) => { close(); P.choose(key); };

	/* Focus moving to another row keeps the panel open; anything else — a click outside, or on
	   nothing at all, which reports no related target — closes it. */
	const onFocusOut = (e:FocusEvent & {currentTarget:HTMLDivElement}) => {
		if (!e.currentTarget.contains(e.relatedTarget as Node|null))
			close();
	};

	const onKeyDown = (e:KeyboardEvent) => {
		if (e.key == 'Escape')
			close();
	};

	return (
		<div class={wrapStyle()} onFocusOut={onFocusOut} onKeyDown={onKeyDown}>
			<button type='button' class={triggerStyle(P.compact,P.plain)}
				onClick={() => open() ? close() : setOpen(true)}
			>
				<Show when={P.prefix}>
					<span>{P.prefix}</span>
				</Show>
				<span class={triggerLabelStyle()}>{P.label || '(none)'}</span>
				<i class='fas fa-caret-down' />
			</button>

			<Show when={open()}>
				{/* Refusing the mousedown leaves the focus on the trigger, so a click on a row is
				    never preceded by a focusout that would close the panel out from under it —
				    browsers disagree on whether clicking a button focuses it. */}
				<div class={panelStyle(P.compact,P.plain,P.panelAlign)} onMouseDown={e => e.preventDefault()}>
					<Show when={drilledGroup()} fallback={<>
						<For each={P.sections.filter(section => section.length > 0)}>{(section,index) => <>
							<Show when={index() > 0}>
								<hr />
							</Show>

							<For each={section}>{row =>
								<button type='button'
									classList={{[rowStyle()]:true, [currentRowStyle()]:row.current}}
									onClick={() => take(row.key)}
								>
									<div>
										<div>{row.label}</div>
										<Show when={row.note}>
											<div class={noteStyle()}>{row.note}</div>
										</Show>
									</div>
								</button>
							}</For>
						</>}</For>

						<Show when={P.groups.length > 0}>
							<Show when={P.sections.some(section => section.length > 0)}>
								<hr />
							</Show>

							<For each={P.groups}>{group =>
								<button type='button' class={rowStyle()}
									onClick={() => setDrilled(group.key)}
								>
									<span>{group.label}</span>
									<span class={leaderStyle()} />
									<i class={`fas fa-chevron-right ${chevronStyle()}`} />
								</button>
							}</For>
						</Show>
					</>}>{group => <>
						<button type='button' class={rowStyle()} onClick={() => setDrilled(undefined)}>
							<i class={`fas fa-chevron-left ${chevronStyle()}`} />
							<span>{group().label}</span>
						</button>

						<hr />

						<For each={group().rows}
							fallback={<div class={emptyStyle()}><i>{group().emptyNote}</i></div>}
						>{row =>
							<button type='button'
								classList={{[rowStyle()]:true, [currentRowStyle()]:row.current}}
								onClick={() => take(row.key)}
							>
								<div>
									<div>{row.label}</div>
									<Show when={row.note}>
										<div class={noteStyle()}>{row.note}</div>
									</Show>
								</div>
							</button>
						}</For>
					</>}</Show>
				</div>
			</Show>
		</div>
	);
}
